import { useState, useRef } from "react";

interface ProductGalleryProps {
  images: string[];
  name: string;
}

export default function ProductGallery({ images, name }: ProductGalleryProps) {
  const [selected, setSelected] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: dir === "left" ? -180 : 180,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Main Image */}
      <div
        className="relative bg-white rounded-xl border border-gray-200 overflow-hidden cursor-zoom-in"
        style={{ height: "420px" }}
        onClick={() => setZoomed(true)}
      >
        <img
          src={images[selected]}
          alt={name}
          className="w-full h-full object-contain p-6 transition-all duration-300"
        />
        <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/80 rounded-full border border-gray-200">
          <i className="ri-zoom-in-line text-gray-500 text-sm"></i>
        </div>
      </div>

      {/* Thumbnails with arrow scroll */}
      {images.length > 1 && (
        <div className="flex items-center gap-2">
          {/* Left Arrow */}
          <button
            onClick={() => scroll("left")}
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-[#F0F7F8] hover:border-[#144E5D] transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-gray-600 text-lg"></i>
          </button>

          {/* Scrollable Strip */}
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto scroll-smooth flex-1"
            style={{ scrollbarWidth: "none" }}
          >
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setSelected(idx)}
                className={`w-20 h-20 flex-shrink-0 rounded-lg border-2 overflow-hidden bg-white transition-all cursor-pointer ${
                  selected === idx
                    ? "border-[#144E5D]"
                    : "border-gray-200 hover:border-[#144E5D]/40"
                }`}
              >
                <img
                  src={img}
                  alt={`${name} ${idx + 1}`}
                  className="w-full h-full object-contain p-1"
                />
              </button>
            ))}
          </div>

          {/* Right Arrow */}
          <button
            onClick={() => scroll("right")}
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-[#F0F7F8] hover:border-[#144E5D] transition-all cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-gray-600 text-lg"></i>
          </button>
        </div>
      )}

      {/* Lightbox */}
      {zoomed && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-8"
          onClick={() => setZoomed(false)}
        >
          <div
            className="relative max-w-2xl w-full bg-white rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setZoomed(false)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-gray-700 text-lg"></i>
            </button>
            <img
              src={images[selected]}
              alt={name}
              className="w-full object-contain p-10"
              style={{ maxHeight: "80vh" }}
            />
            {images.length > 1 && (
              <div className="flex justify-center gap-2 pb-4">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelected(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${selected === idx ? "bg-[#144E5D]" : "bg-gray-300"}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
